"use client";

// SIDEBAR-TOGGLE-FIXED (2026-10-30): the toggle leads the header; collapsed, the panel becomes a
// rail-wide strip (toggle at the same spot, the mode name downwards, the `strip` rows as icons).
// The mode's sidebar panel (design spec §4): a 40 px header with the sidebar toggle, fixed
// rows, a scroll area and a footer. Rows are 36 px (44 on coarse pointers), 8 px side margin,
// radius 8, 13 px text and an 18 px icon; captions are DTP's 11 px / 600 / 1.2 px uppercase.
// Title and captions are not headings: the page owns the outline, the `nav` names the panel.
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../cn";
import { SidebarToggleButton, useShellLayout } from "./app-shell";
import { CountPill } from "./count-pill";
import { FOCUS_RING_INSET } from "./focus-ring";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/** True inside the collapsed strip: rows draw icon-only, captions draw a hairline. */
const StripContext = React.createContext(false);

function SidebarPanel({
  title,
  actions,
  footer,
  strip,
  children,
  className,
  "aria-label": label,
}: {
  title: string;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * SIDEBAR-TOGGLE-FIXED: what the collapsed strip lists under the mode name — the mode's main
   * `NavRow`s (they draw icon-only with a tooltip and an unread dot; a `SidebarCaption` draws a
   * hairline). Absent: the strip holds the toggle and the mode name only.
   */
  strip?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /** The navigation landmark's name; defaults to the title. */
  "aria-label"?: string;
}) {
  const { sidebarCollapsed } = useShellLayout();
  return (
    <>
      <nav
        aria-label={label ?? title}
        data-slot="sidebar-panel"
        className={cn(
          "flex min-h-0 flex-1 flex-col lg:group-data-[sidebar=collapsed]/shell:hidden",
          className,
        )}
      >
        <PanelHeader title={title}>{actions}</PanelHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pb-3 scrollbar-gutter-stable">
          {children}
        </div>
        {footer ? <SidebarPanelFooter>{footer}</SidebarPanelFooter> : null}
      </nav>
      {/* Collapsed (wide screens only; the phone drawer always shows the whole panel). */}
      {sidebarCollapsed ? (
        <SidebarStrip title={title} label={label ?? title}>
          {strip}
        </SidebarStrip>
      ) : null}
    </>
  );
}

/**
 * The collapsed sidebar: a rail-wide strip with the toggle at the very spot it has in the open
 * header, the mode name written downwards (a click opens the panel), then the strip's rows.
 */
function SidebarStrip({
  title,
  label,
  children,
}: {
  title: string;
  label: string;
  children?: React.ReactNode;
}) {
  const { toggleSidebar } = useShellLayout();
  return (
    <nav
      aria-label={`${label}, collapsed`}
      data-slot="sidebar-strip"
      className="hidden min-h-0 flex-1 flex-col lg:group-data-[sidebar=collapsed]/shell:flex"
    >
      <div className="flex h-10 shrink-0 items-center pl-3.5">
        <SidebarToggleButton action="show" />
      </div>
      <button
        type="button"
        aria-label={`Show the ${title} sidebar`}
        data-slot="sidebar-strip-title"
        onClick={() => toggleSidebar(false)}
        className={cn(
          "mx-auto mt-1 shrink-0 rounded-md px-1.5 py-2 text-sm font-semibold text-foreground [writing-mode:vertical-rl] hover:bg-muted",
          FOCUS_RING_INSET,
        )}
      >
        {title}
      </button>
      <div className="no-scrollbar mt-2 min-h-0 flex-1 overflow-y-auto pb-3">
        <StripContext.Provider value={true}>{children}</StripContext.Provider>
      </div>
    </nav>
  );
}

function PanelHeader({
  title,
  children,
  className,
}: {
  title: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="panel-header"
      // The toggle leads, at the strip's spot (14 px in, centred in the 40 px row).
      className={cn("flex h-10 shrink-0 items-center gap-1 pr-2 pl-3.5", className)}
    >
      <SidebarToggleButton action="hide" />
      <div className="ml-0.5 min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function SidebarCaption({
  label,
  action,
  className,
}: {
  label: string;
  action?: React.ReactNode;
  className?: string;
}) {
  // In the strip a section is a hairline between groups of icons.
  if (React.useContext(StripContext))
    return <hr data-slot="sidebar-caption" className="mx-3 my-2 border-subtle" />;
  return (
    <div
      data-slot="sidebar-caption"
      className={cn("mt-4 flex h-7 items-center justify-between pr-2 pl-4", className)}
    >
      <div className="truncate text-2xs font-semibold tracking-caps text-muted-foreground uppercase">
        {label}
      </div>
      {action}
    </div>
  );
}

/** A list of `NavRow`s. */
function NavList({ className, ...props }: React.ComponentProps<"ul">) {
  return <ul data-slot="nav-list" className={cn("flex flex-col gap-px", className)} {...props} />;
}

const ROW = `group/nav-row relative mx-2 flex min-h-9 items-center gap-2.5 rounded-md px-2 text-sm text-sidebar-foreground transition-colors duration-(--dur-fast) ease-out hover:bg-muted hover:text-foreground pointer-coarse:min-h-11 aria-[current=page]:bg-primary-soft aria-[current=page]:font-medium aria-[current=page]:text-primary-text ${FOCUS_RING_INSET}`;

type NavRowProps = useRender.ComponentProps<"a"> & {
  icon?: LucideIcon;
  /** Drawn in the icon slot instead of an icon (a company tile, an avatar). */
  lead?: React.ReactNode;
  label: string;
  active?: boolean;
  count?: number;
  /** Something new here (the strip shows a dot; `count` > 0 does too). */
  dot?: boolean;
  badge?: React.ReactNode;
  /** Trailing content (e.g. an external-link mark). */
  trailing?: React.ReactNode;
};

const STRIP_ROW = `group/nav-row relative mx-auto flex size-9 items-center justify-center rounded-md text-sidebar-foreground transition-colors duration-(--dur-fast) ease-out hover:bg-muted hover:text-foreground pointer-coarse:size-11 aria-[current=page]:bg-primary-soft aria-[current=page]:text-primary-text ${FOCUS_RING_INSET}`;

function NavRow({
  icon: Icon,
  lead,
  label,
  active,
  count,
  dot,
  badge,
  trailing,
  className,
  render,
  ...props
}: NavRowProps) {
  const strip = React.useContext(StripContext);
  // SIDEBAR-TOGGLE-FIXED: in the collapsed strip, the row is its icon (or lead, or initial),
  // named by a tooltip, with a dot for anything unread.
  const stripRow = useRender({
    defaultTagName: "a",
    render,
    enabled: strip,
    props: mergeProps<"a">(
      {
        "aria-current": active ? "page" : undefined,
        className: STRIP_ROW,
        children: (
          <>
            {lead ??
              (Icon ? (
                <Icon
                  aria-hidden="true"
                  className="size-[18px] shrink-0 text-faint group-hover/nav-row:text-secondary group-aria-[current=page]/nav-row:text-primary-text"
                />
              ) : (
                <span aria-hidden="true" className="text-sm font-semibold">
                  {label.replace(/^#/, "").charAt(0).toUpperCase()}
                </span>
              ))}
            <span className="sr-only">{label}</span>
            {dot || (count !== undefined && count > 0) ? (
              <span
                aria-hidden="true"
                data-slot="nav-row-dot"
                className="absolute top-1 right-1 size-2 rounded-full bg-primary ring-2 ring-sidebar"
              />
            ) : null}
          </>
        ),
      },
      props,
    ),
    state: { slot: "nav-row" },
  });
  const row = useRender({
    enabled: !strip,
    defaultTagName: "a",
    render,
    props: mergeProps<"a">(
      {
        "aria-current": active ? "page" : undefined,
        className: cn(ROW, className),
        children: (
          <>
            {lead}
            {Icon ? (
              <Icon
                aria-hidden="true"
                className="size-[18px] shrink-0 text-faint group-hover/nav-row:text-secondary group-aria-[current=page]/nav-row:text-primary-text"
              />
            ) : null}
            <span className="min-w-0 flex-1 truncate">{label}</span>
            {badge}
            {count !== undefined && count > 0 ? <CountPill count={count} /> : null}
            {trailing}
          </>
        ),
      },
      props,
    ),
    state: { slot: "nav-row" },
  });
  if (strip && stripRow)
    return (
      <li data-slot="nav-row" className="flex">
        <Tooltip>
          <TooltipTrigger render={stripRow} />
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </li>
    );
  return <li data-slot="nav-row">{row}</li>;
}

function SidebarPanelFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-panel-footer"
      className={cn("shrink-0 border-t border-subtle py-2", className)}
      {...props}
    />
  );
}

/**
 * UX-A (ux pass 2.2): a sidebar section's empty state, one wording everywhere: "No {things}
 * yet." and, when a "+" beside the caption makes one, the action ("Add one with +").
 */
function SidebarEmpty({
  things,
  action,
  className,
}: {
  things: string;
  action?: string;
  className?: string;
}) {
  if (React.useContext(StripContext)) return null;
  return (
    <p
      data-slot="sidebar-empty"
      className={cn("px-4 py-1 text-xs text-muted-foreground", className)}
    >
      {`No ${things} yet.`}
      {action ? ` ${action}.` : null}
    </p>
  );
}

export {
  NavList,
  NavRow,
  PanelHeader,
  SidebarCaption,
  SidebarEmpty,
  SidebarPanel,
  SidebarPanelFooter,
};
